import { FetchError } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import {
  QueryKey,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { UserInfo } from "types"
import { GetUserResponse, UserQueryParams } from "utils/api/client/types/user"
import { apiClient } from "../client"
import { queryKeysFactory } from "../client/query-key-factory"
import { queryClient } from "../client/query-client"
import { getPagination } from "utils/helpers/common"

const USERS_QUERY_KEY = "users" as const
export const usersQueryKeys = {
  ...queryKeysFactory(USERS_QUERY_KEY),
  me: () => [USERS_QUERY_KEY, "me"],
}

export const useMe = (
  query?: HttpTypes.AdminUserParams,
  options?: UseQueryOptions<UserInfo, FetchError, UserInfo, QueryKey>
) => {
  const { data, ...rest } = useQuery({
    queryFn: () => apiClient.vendor.user.me(query),
    queryKey: usersQueryKeys.me(),
    ...options,
  })

  return {
    user: data as unknown as UserInfo,
    ...rest,
  }
}

export const useGetUserByEmail = ({ vendorId }: { vendorId?: string }) =>
  useMutation<GetUserResponse, unknown, UserQueryParams>({
    mutationKey: usersQueryKeys.list(),
    mutationFn: (payload) => apiClient.pomAdmin.user.list(payload, vendorId),
  })

export const useUserDetail = (params: { id: string }) =>
  useQuery({
    queryKey: usersQueryKeys.detail(params.id),
    queryFn: async ({ queryKey }) => {
      const userId = queryKey?.[2] || ""

      if (!userId) return
      const res = await apiClient.pomAdmin.user.retrieve({ id: userId })
      return res.data
    },
  })

export const useCreateUser = () => {
  const createUser = async ({ body }: { body: any }) => {
    return apiClient.pomAdmin.user.create(body)
  }

  return useMutation({
    mutationFn: createUser,
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: usersQueryKeys.list(),
      })
    },
  })
}

export const useEditUser = () => {
  const editUser = async ({ id, body }: { id: string; body: any }) => {
    return apiClient.pomAdmin.user.update(id, body)
  }

  return useMutation({
    mutationFn: editUser,
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: usersQueryKeys.list(),
      })
      queryClient.invalidateQueries({
        queryKey: usersQueryKeys.detail(id),
      })
    },
  })
}

export const useDeleteUser = () => {
  const deleteUser = async ({ id }: { id: string }) => {
    if (!id) return
    return apiClient.pomAdmin.user.delete(id)
  }

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: usersQueryKeys.list(),
      })
    },
  })
}

export const useListUsers = (params: UserQueryParams) =>
  useQuery({
    queryKey: usersQueryKeys.list(params),
    queryFn: async ({ queryKey }) => {
      const { limit, offset, ...rest } = (queryKey?.[2].query || {}) as any

      const res = await apiClient.pomAdmin.user.list({
        limit,
        offset,
        ...rest,
      })
      const metadata = getPagination({
        count: res.count,
        limit: res.limit,
        offset: res.offset,
      })

      return {
        data: res?.users ?? [],
        metadata,
      }
    },
  })
